import { useQuery } from '@tanstack/react-query';
import type { CreatorListEligibilityResponse, ViewEligibility } from '@aegislab/shared';
import { useAuthStore } from '@/shared/model';
import { getCreatorListEligibility } from '../api/creatorListApi';
import { creatorListKeys } from './creatorListKeys';

/** 자격은 5분 들고 있는다 — 팀장에서 빠져도 다음 조회 전까지는 남는다(IA §1, 서버가 요청마다 다시 막는다). */
const ELIGIBILITY_STALE_MS = 5 * 60 * 1000;
/** 조회가 실패하면 이 뒤에 다시 묻는다. 그사이 독은 그 항목을 숨긴 채로 둔다. */
const ELIGIBILITY_RETRY_MS = 30 * 1000;

/**
 * 명단 내려받기 자격 — docs/design/ia/creator-lists-ia.md §1.
 *
 * 독·빠른 이동·창 가드·미리 불러오기가 이 값 하나를 `ViewPermissionGrants.eligibilities` 로 받는다.
 * 실패는 전역 토스트로 알리지 않는다 — 독의 「권한 정보를 불러오지 못했습니다」 줄도 띄우지 않고
 * 30초 뒤 조용히 다시 묻는다. 창 가드만 실패를 문장으로 보인다.
 */
export const useCreatorListEligibility = () => {
  const isAuthed = useAuthStore((st) => !!st.user);
  return useQuery({
    queryKey: creatorListKeys.eligibility(),
    queryFn: getCreatorListEligibility,
    staleTime: ELIGIBILITY_STALE_MS,
    refetchOnWindowFocus: false,
    retry: false,
    refetchInterval: (query) => (query.state.status === 'error' ? ELIGIBILITY_RETRY_MS : false),
    enabled: isAuthed,
    meta: { skipGlobalErrorToast: true },
  });
};

/** 자격 조회 결과 → 창 판정 입력. 확인 전·실패는 자격 없음과 같게 숨긴다(fail-closed). */
export function toViewEligibilities(
  data: CreatorListEligibilityResponse | undefined
): ViewEligibility[] {
  return data?.canDownload ? ['creator-list'] : [];
}
